/**
 * DateRangeFilter plugin.
 *
 * Filters grant calls by application end date range (from/to).
 * URL/API/SavedView param names: applicationEndDateFrom, applicationEndDateTo (same across all).
 *
 * @see docs/specifications/2026-03-10-plugin-filter-architecture.md (Section 4)
 */

import React, { useCallback } from "react";
import { FilterPlugin, FilterComponentProps } from "../types";
import { SavedViewFilters } from "../../../../../services/grantsApi";

/**
 * Value type for the date range filter.
 */
export interface DateRangeValue {
  from: string;
  to: string;
}

/**
 * Date range filter React component.
 * Renders two date inputs (from/to) for application end date range.
 */
const DateRangeFilterComponent = React.memo(function DateRangeFilterComponent(
  props: FilterComponentProps<DateRangeValue>
) {
  const { value, onChange } = props;

  const handleFromChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ ...value, from: e.target.value });
    },
    [value, onChange]
  );

  const handleToChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ ...value, to: e.target.value });
    },
    [value, onChange]
  );

  return React.createElement("div", null,
    React.createElement("label", {
      className: "block text-sm font-medium text-neutral-700 mb-2",
    }, "Data zakończenia naboru"),
    React.createElement("div", { className: "grid grid-cols-2 gap-4" },
      React.createElement("div", null,
        React.createElement("label", {
          htmlFor: "applicationEndDateFrom",
          className: "block text-xs text-neutral-600 mb-1",
        }, "Od"),
        React.createElement("input", {
          type: "date",
          id: "applicationEndDateFrom",
          value: value.from,
          onChange: handleFromChange,
          className: "w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:outline-none",
        })
      ),
      React.createElement("div", null,
        React.createElement("label", {
          htmlFor: "applicationEndDateTo",
          className: "block text-xs text-neutral-600 mb-1",
        }, "Do"),
        React.createElement("input", {
          type: "date",
          id: "applicationEndDateTo",
          value: value.to,
          onChange: handleToChange,
          className: "w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:outline-none",
        })
      )
    ),
    React.createElement("p", {
      className: "mt-1 text-xs text-neutral-500",
    }, "Znajdź nabory kończące się w określonym przedziale czasowym")
  );
});

export const DateRangeFilterPlugin: FilterPlugin<DateRangeValue> = {
  key: "dateRange",
  label: "Data zakończenia naboru",
  placement: "advanced",
  order: 4,
  defaultValue: { from: "", to: "" },
  Component: DateRangeFilterComponent,

  isActive: (value: DateRangeValue): boolean => {
    return value.from !== "" || value.to !== "";
  },

  toUrlParams: (value: DateRangeValue, params: URLSearchParams): void => {
    if (value.from) {
      params.set("applicationEndDateFrom", value.from);
    }
    if (value.to) {
      params.set("applicationEndDateTo", value.to);
    }
  },

  fromUrlParams: (params: URLSearchParams): DateRangeValue => {
    return {
      from: params.get("applicationEndDateFrom") ?? "",
      to: params.get("applicationEndDateTo") ?? "",
    };
  },

  toSavedView: (
    value: DateRangeValue,
    filters: Partial<SavedViewFilters>
  ): void => {
    if (value.from) {
      filters.applicationEndDateFrom = value.from;
    }
    if (value.to) {
      filters.applicationEndDateTo = value.to;
    }
  },

  fromSavedView: (filters: SavedViewFilters): DateRangeValue => {
    return {
      from: filters.applicationEndDateFrom ?? "",
      to: filters.applicationEndDateTo ?? "",
    };
  },

  toApiParams: (value: DateRangeValue, params: URLSearchParams): void => {
    if (value.from) {
      params.set("applicationEndDateFrom", value.from);
    }
    if (value.to) {
      params.set("applicationEndDateTo", value.to);
    }
  },
};
